import { Measure } from '@cbsi-cbscom/measure';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { Metrics } from 'library/ts/utilities/measure/Metrics';
import { Throttler } from 'library/ts/utilities/modules/Function';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { LoadRequestReason } from 'tag/ts/tag/api/video/LoadRequestReason';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { IVideoAPIUtilities } from 'tag/ts/tag/interfaces/api/IVideoAPIUtilities';
import { IVideoLoadManager } from 'tag/ts/tag/interfaces/api/IVideoLoadManager';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ICallbacksVO } from 'tag/ts/tag/interfaces/callbacks/ICallbacksVO';
import { ILoadRequest } from 'tag/ts/tag/interfaces/model/ILoadRequest';
import { ITagOnceSettings } from 'tag/ts/tag/interfaces/model/ITagOnceSettings';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { TagErrorCode } from 'tag/ts/tag/model/error/TagErrorCode';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Manages the load() request lifecycle: throttling, concurrency guard, last-wins
 * pending slot, and provenance tracing.
 *
 * **Responsibilities:**
 * - Throttle rapid load() calls (default 50ms, configurable via TAG.loadingThrottleMS)
 * - Gate concurrent player creation via #isPlayerCreating; queue the latest call in #pendingLoadRequest
 * - Stamp each load() call with ILoadRequest provenance milestones (calledAt, queuedAt, executedAt, etc.)
 * - Drain the pending slot after creation succeeds or creation-failure cleanup completes
 * - Expose rolling load history at tag.snapshot.loadHistory
 *
 * **Separation of concerns:**
 * VideoAPI owns the public API surface (IN getter). VideoLoadManager owns the load lifecycle.
 * Same pattern as AviaAdapter → AviaLifecycleManager.
 */
export class VideoLoadManager extends AbstractTagClass implements IVideoLoadManager {

	static readonly #LOG_PREFIX = 'load:';

	// ─── Throttle ────────────────────────────────────────────────────────────────

	/**
	 * @description Fallback throttle (ms) used only when TAG.loadingThrottleMS is absent from params.
	 * Effective runtime default is 50ms — set by ParamsVO.loadingThrottleMS at create-time.
	 * This constant is the last-resort floor; it is not the value consumers typically run with.
	 * Configurable via TAG.loadingThrottleMS; disable with TAG.loadingThrottleEnabled: false.
	 */
	static readonly #DEFAULT_LOAD_THROTTLE_MS: number = 500;
	#loadThrottler: Throttler;

	// ─── Concurrency ─────────────────────────────────────────────────────────────

	/**
	 * @description True from the moment load() executes until ON_CREATE_PLAYER_SUCCESS /
	 * ON_CREATE_PLAYER_FAILURE handling completes. Guards the pending slot.
	 */
	#isPlayerCreating: boolean = false;

	/**
	 * @description Holds the most-recent load() call that arrived while #isPlayerCreating.
	 * Last-wins: each new arrival overwrites the slot, marking the previous as discarded.
	 * Drained when the in-flight creation completes.
	 */
	#pendingLoadRequest: {
		params: IParamsVO;
		callbacks: ICallbacksVO;
		onceSettings: ITagOnceSettings;
		record: ILoadRequest;
		resolve: ( value: IPromiseResolveGeneric ) => void;
		reject: ( reason?: unknown ) => void;
	} | null = null;

	// ─── Provenance tracing ───────────────────────────────────────────────────────

	/** @description Default cap on load history entries. Configurable via TAG.loadingHistoryMax. */
	static readonly #DEFAULT_LOAD_HISTORY_MAX: number = 20;

	#loadHistoryMax: number = VideoLoadManager.#DEFAULT_LOAD_HISTORY_MAX;
	#loadCallCounter: number = 0;
	#activeRecord: ILoadRequest | null = null;
	/**
	 * @description Holds the record that was just pushed to history on ON_CREATE_PLAYER_SUCCESS,
	 * kept alive so that the later RESOURCE_START event can stamp `playbackAt` on it.
	 * Set in `#onPlayerCreationComplete`, cleared in `#onPlayerEvent` on RESOURCE_START.
	 * The record is already in `#loadHistory` — stamping it here is reflected in the array.
	 */
	#awaitingPlayback: ILoadRequest | null = null;
	#loadHistory: ILoadRequest[] = [];

	// ─── Dependencies ─────────────────────────────────────────────────────────────

	#videoAPIUtilities: IVideoAPIUtilities;

	constructor( argument: ITagAbstractClassArgument & { videoAPIUtilities: IVideoAPIUtilities } ) {
		super( { ...argument, name: 'VideoLoadManager' } );

		this.#videoAPIUtilities = argument.videoAPIUtilities;

		let loadThrottleMS = this?.tag?.params?.TAG?.loadingThrottleMS || VideoLoadManager.#DEFAULT_LOAD_THROTTLE_MS;
		if ( this?.tag?.params?.TAG?.loadingThrottleEnabled === false ) {
			loadThrottleMS = 0;
		}
		this.#loadThrottler = new this.utils.Function.Throttler( loadThrottleMS );

		if ( this.utils.Type.isNumber( this?.tag?.params?.TAG?.loadingHistoryMax ) ) {
			this.#loadHistoryMax = this.tag.params.TAG.loadingHistoryMax;
		}

		this.tagEventsHandler(
			this.#onPlayerCreationComplete,
			[
				TagEvent.ON_CREATE_PLAYER_SUCCESS,
				TagEvent.ON_CREATE_PLAYER_FAILURE
			]
		);

		this.tagEventsHandler(
			this.#onPlayerEvent,
			[ TagEvent.ON_PLAYER_EVENT ]
		);
	}

	// ─── Public API ──────────────────────────────────────────────────────────────

	/**
	 * @description Entry point for a host load() call. Applies throttle, enqueues if creation
	 * is in flight (last-wins), or executes immediately. Stamps provenance records throughout.
	 *
	 * @returns A Promise that reflects the actual outcome of the load:
	 * - **Throttled** — rejects with an Error immediately.
	 * - **Queued** — resolves/rejects when the pending slot drains and the dequeued call
	 *   completes. Superseded callers reject with `{ msg: 'superseded', success: false }`.
	 * - **Immediate** — resolves with `{ msg: 'done' }` once `ON_API_LOAD_VIDEO_REQUEST`
	 *   is dispatched, or rejects on `DISALLOWED` / `STOP_PLAYER_FAILED`.
	 */
	load = ( params: IParamsVO = {} as IParamsVO, callbacks: ICallbacksVO = {} as ICallbacksVO, onceSettings: ITagOnceSettings = {} ): Promise<IPromiseResolveGeneric> => {
		const record = this.#createRecord();

		if ( !this.#loadThrottler.canExecute() ) {
			record.discardedAt = Date.now();
			record.reason = LoadRequestReason.THROTTLED;
			this.#pushToHistory( record );
			return Promise.reject( new Error( 'tag.API.VIDEO.load( params ) call was rejected due to rapid succession!' ) );
		}

		if ( this.#isPlayerCreating ) {
			if ( this.#pendingLoadRequest ) {
				this.#pendingLoadRequest.record.discardedAt = Date.now();
				this.#pendingLoadRequest.record.reason = LoadRequestReason.SUPERSEDED;
				this.#pushToHistory( this.#pendingLoadRequest.record );
				this.#pendingLoadRequest.reject( { msg: 'superseded', success: false } as IPromiseResolveGeneric );
			}
			record.queuedAt = Date.now();
			this?.logger?.log( VideoLoadManager.#LOG_PREFIX, 'queued (creation in flight) — id:', record.id );
			return new Promise<IPromiseResolveGeneric>( ( resolve, reject ) => {
				this.#pendingLoadRequest = {
					params, callbacks, onceSettings, record, resolve, reject
				};
			} );
		}

		record.executedAt = Date.now();
		this.#activeRecord = record;
		this.#isPlayerCreating = true;

		return this.#execute( params, callbacks, onceSettings );
	};

	get loadHistory(): ILoadRequest[] {
		return this.#loadHistory;
	}

	// ─── Provenance helpers ───────────────────────────────────────────────────────

	#createRecord = (): ILoadRequest => {
		this.#loadCallCounter++;
		return { id: this.#loadCallCounter, calledAt: Date.now() };
	};

	#pushToHistory = ( record: ILoadRequest ): void => {
		this.#loadHistory.push( record );
		if ( this.#loadHistory.length > this.#loadHistoryMax ) {
			this.#loadHistory.shift();
		}
	};

	// ─── Concurrency helpers ──────────────────────────────────────────────────────

	/**
	 * @description Stamps the active record with a failure reason, resets concurrency state,
	 * rejects the caller's promise, and drains the pending slot. Single point of truth for
	 * all failure paths in #execute so no path can forget a step.
	 */
	#rectifyStateOnFailure = ( reason: LoadRequestReason, reject: ( value?: unknown ) => void, error?: unknown ): void => {
		if ( this.#activeRecord ) {
			this.#activeRecord.reason = reason;
			this.#pushToHistory( this.#activeRecord );
			this.#activeRecord = null;
		}
		this.#isPlayerCreating = false;
		reject( error ?? { msg: reason, success: false } as IPromiseResolveGeneric );
		this.#drainPendingSlot();
	};

	/**
	 * @description Settles a load() call blocked by the JWT guard by RESOLVING rather than
	 * rejecting — same loadHistory/concurrency bookkeeping as #rectifyStateOnFailure, but the
	 * caller's promise resolves with `success: false` instead of rejecting. Deliberately
	 * inconsistent with every other #execute() failure path (THROTTLED/DISALLOWED/
	 * STOP_PLAYER_FAILED/CREATION_FAILED all reject) — see #dispatchMissingJwtFatalError for why.
	 */
	#resolveStateOnJwtGuardBlock = ( resolve: ( value: IPromiseResolveGeneric ) => void ): void => {
		if ( this.#activeRecord ) {
			this.#activeRecord.reason = LoadRequestReason.MISSING_JWT;
			this.#pushToHistory( this.#activeRecord );
			this.#activeRecord = null;
		}
		this.#isPlayerCreating = false;
		resolve( { msg: 'blocked-missing-jwt', success: false } as IPromiseResolveGeneric );
		this.#drainPendingSlot();
	};

	/**
	 * @description Releases the player-creation guard and drains the last-wins pending slot.
	 * Shared by immediate success/no-active-record completion and delayed active-load failure cleanup.
	 */
	#releaseCreationGuardAndDrain = (): void => {
		this.#isPlayerCreating = false;
		this.#drainPendingSlot();
	};

	/**
	 * @description Defers active-load failure cleanup by one tick so all listeners can inspect the
	 * failed content identity. Missing/non-object CONTENT skips clearContentProperties because that
	 * helper does not settle without an object.
	 */
	#clearContentAfterPlayerCreationFailure = async (): Promise<void> => {
		await this.utils.Delay.delay();
		if ( this.utils.Type.isObject( this.tag?.params?.CONTENT ) ) {
			await this.#videoAPIUtilities.clearContentProperties();
		}
	};

	/**
	 * @description Drains the pending load slot — dequeues the waiting call, re-arms concurrency
	 * state, and re-invokes #execute, forwarding the outcome to the pending caller's promise.
	 * Called after creation success, after delayed creation-failure cleanup, and from all #execute
	 * failure paths so every exit resets consistently.
	 */
	#drainPendingSlot = (): void => {
		if ( !this.#pendingLoadRequest ) {
			return;
		}
		const pending = this.#pendingLoadRequest;
		this.#pendingLoadRequest = null;
		const now = Date.now();
		pending.record.dequeuedAt = now;
		pending.record.executedAt = now;
		this.#activeRecord = pending.record;
		this.#isPlayerCreating = true;
		this?.logger?.log( VideoLoadManager.#LOG_PREFIX, 'draining pending slot — id:', pending.record.id );
		// Delay drain by one tick so all ON_CREATE_PLAYER_SUCCESS listeners (e.g. SkinController
		// calling getResourceConfig) complete before a pending load clears tag.params.CONTENT.
		// Active-load creation failures reach this point only after listener-safe cleanup completes.
		this.utils.Delay.delay().then( () => {
			this.#execute( pending.params, pending.callbacks, pending.onceSettings )
				.then( pending.resolve )
				.catch( pending.reject );
		} ).catch( ( error ) => {
			this?.logger?.warn( VideoLoadManager.#LOG_PREFIX, 'drain execute ERROR:', error );
			pending.reject( error );
		} );
	};

	// ─── JWT guard (PLBCK-8587) ────────────────────────────────────────────────────

	/**
	 * @description Returns whether a token value is unusable — not a string, or an empty string.
	 * Presence/type check only (null, undefined, non-string, empty string) — structural JWT
	 * validation (segment count, decode, expiry) is deliberately out of scope for this pass (v1).
	 *
	 * Extension point: additional validation methods (segment-count check, decode + `exp` check,
	 * signature/format checks, etc.) belong here as further conditions, not as a parallel guard —
	 * this is the single place `#isJwtTokenMissing` calls to decide "is this token usable." If a
	 * future check flags a non-empty token as invalid (e.g. expired), do NOT put the token value
	 * anywhere it could reach a log message or thrown Error — see the message construction in
	 * #dispatchMissingJwtFatalError, which deliberately reports content id, never the token, and
	 * that rule holds regardless of which check is what flagged the token invalid.
	 */
	#isMissingOrInvalidToken = ( token: unknown ): boolean => {
		if ( !this.utils.Type.isString( token ) ) {
			return true;
		}
		// Type.isString() above is a plain boolean predicate, not a TS type-predicate (`value is
		// string`) — true of every check in this codebase's Type module — so it doesn't narrow
		// `token` for TS. Safe here specifically because the cast only runs after that check has
		// already confirmed it at runtime.
		return this.utils.Type.isEmptyString( token as string );
	};

	/**
	 * @description Returns whether this load carries Stitcher params. Checks the current load's
	 * own `params` argument — NOT the inherited `hasStitcherParams()` (AbstractTagClass), which
	 * reads `this.tag.params.STITCHER`, the aggregate that `TagModel#checkParams` only merges in
	 * reactively once ON_API_LOAD_VIDEO_REQUEST has dispatched. On a Tag's first-ever load, that
	 * aggregate is still empty even for genuine Stitcher content, since TN Web supplies STITCHER
	 * via `load()` params, not at Tag construction — confirmed via live testing (PLBCK-8587):
	 * `hasStitcherParams()` logged `false` while this same load's own `params.STITCHER` carried a
	 * fully-populated Pluto channel object. Using the aggregate here would silently never fire the
	 * guard on a Tag's first load — exactly the cold-start scenario this guard exists to catch.
	 */
	#isStitcherLoad = ( params: IParamsVO ): boolean => {
		return !this.utils.Type.isEmpty( params?.STITCHER );
	};

	/**
	 * @description Returns whether TAG.invalidJwtLoadGuard is enabled and no usable token exists
	 * for this load. Resolves the SAME single effective token Blackbird's SessionJsonPlugin
	 * actually sends (`getToken()`: `window.jwtToken ?? params.STITCHER.token`) and validates
	 * THAT value — not each token independently. That distinction matters: `??` only falls back
	 * on `null`/`undefined`, not on falsy-but-defined values like `''`. An earlier version of this
	 * check validated `window.jwtToken` and `STITCHER.token` separately and only fired when BOTH
	 * failed independently — so `window.jwtToken === ''` alongside a valid `STITCHER.token` was
	 * treated as "fine" here, while SessionJsonPlugin's `??` would still resolve to `''` (`??`
	 * doesn't skip empty string) and send that unusable value to Blackbird regardless of the valid
	 * `STITCHER.token` sitting unused right next to it (caught in code review, PR #4119). Resolving
	 * the same effective value first and validating once avoids that class of mismatch entirely —
	 * whatever SessionJsonPlugin would actually send is exactly what gets checked.
	 *
	 * window.jwtToken is legitimately undefined by design on P+/CBS.com paths, where the real
	 * token lives in STITCHER.token — this is exactly the case `??` (not independent validation)
	 * needs to fall through correctly. Reads STITCHER.token off the current load's own `params`
	 * argument, not `this.tag.params` — the latter still holds the previous load's value at this
	 * point in the pipeline; TagModel#checkParams only merges the new STITCHER bucket in reactively,
	 * in response to the ON_API_LOAD_VIDEO_REQUEST dispatch this check gates.
	 *
	 * Additionally gated on `#isStitcherLoad` — P+/CBS.com loads carry neither window.jwtToken nor
	 * STITCHER.token as their auth mechanism at all, so without this gate a host that mistakenly
	 * enabled the flag for a non-Stitcher embed would fatal-error every load unconditionally, not
	 * just genuinely-broken ones.
	 */
	#isJwtTokenMissing = ( params: IParamsVO ): boolean => {
		if ( this.tag?.params?.TAG?.invalidJwtLoadGuard !== true || !this.#isStitcherLoad( params ) ) {
			return false;
		}
		const jwtToken = ( window as Window & { jwtToken?: string } ).jwtToken;
		const stitcherToken = params?.STITCHER?.token;
		const effectiveToken = jwtToken ?? stitcherToken;
		return this.#isMissingOrInvalidToken( effectiveToken );
	};

	/**
	 * @description Fatal-errors a load() call blocked by the JWT guard — RESOLVES the caller's
	 * promise (via #resolveStateOnJwtGuardBlock) before dispatching ON_FATAL_ERROR, rather than
	 * rejecting the way every other #execute() failure path does. This is a deliberate exception
	 * to that pattern, not an oversight.
	 *
	 * Why: hosts commonly gate their own load()-retry suppression on the promise having resolved
	 * (e.g. "already loaded, don't call load() again" style guards keyed on a resolved-not-rejected
	 * prior attempt). A rejected promise leaves that guard unarmed, so a host whose own re-render
	 * cycle re-invokes its load-triggering effect for unrelated reasons (e.g. a dependency that
	 * changes identity on every render) will call tag.API.VIDEO.load() again, hit the same missing
	 * token, reject again, and repeat — an unbounded loop with no backoff, entirely on the host
	 * side but only reachable because our rejection left its guard unarmed. Resolving first arms
	 * that guard exactly the way a real downstream avia-js fatal already does (that promise also
	 * resolves before the player-level error ever fires), so this failure mode now behaves like
	 * every other fatal from the host's perspective instead of being the one path that can loop.
	 *
	 * The resolved value carries `success: false` so a caller that actually inspects it (unlike a
	 * host that only checks whether the promise threw) can still distinguish this from a real
	 * success.
	 *
	 * Ordering matters: ON_FATAL_ERROR dispatch is deferred one real tick (this.utils.Delay.delay(),
	 * a genuine setTimeout under the hood — see Delay.ts) so every microtask queued by the resolved
	 * promise — including the host's own await continuation — has already run before the fatal
	 * event (and any host-side reaction to it) fires. Dispatching synchronously alongside the
	 * resolve risks the host's fatal-error handling reacting before its own promise-resolution
	 * bookkeeping has committed, which would defeat the point of resolving first.
	 *
	 * Still fires the deferred genuinely-uncaught throw so NewRelic/DataDog's passive
	 * window.onerror capture can see it — unaffected by resolve-vs-reject, since it's a raw
	 * setTimeout throw with no relationship to the promise at all.
	 */
	#dispatchMissingJwtFatalError = ( resolve: ( value: IPromiseResolveGeneric ) => void, params: IParamsVO ): void => {
		// Content id (not the token — see #isMissingOrInvalidToken's neighboring docs on why the
		// token itself never belongs in a message) so whoever sees this error, in any context, knows
		// which channel/movie/episode was blocked. STITCHER.id + STITCHER.type together identify all
		// three content kinds uniformly — there's no separate channelId/movieId/episodeId field.
		const contentDescriptor = `${ params?.STITCHER?.type ?? 'unknown-type' }/${ params?.STITCHER?.id ?? 'unknown-id' }`;
		const message = `TagErrorCode ${ TagErrorCode.MISSING_JWT_TOKEN }: tag.API.VIDEO.load() blocked — no usable token (effective window.jwtToken ?? STITCHER.token missing/invalid) — content: ${ contentDescriptor }`;

		// Co-located with the warn itself (rather than relying solely on #execute()'s own
		// unconditional top-of-call paramsVO log) so QA/dev inspection can correlate exactly which
		// attempted load's params triggered this specific fatal, without scrolling back through the
		// log or correlating timestamps.
		this?.logger?.warn( VideoLoadManager.#LOG_PREFIX, message, 'blocked paramsVO:', params );

		this.#resolveStateOnJwtGuardBlock( resolve );

		this.utils.Delay.delay().then(
			() => {
				this.tag.dispatchTagEvent(
					this.tag.getTagEventVO(
						TagEvent.ON_FATAL_ERROR,
						this.tag,
						{
							fatalError: {
								code: TagErrorCode.MISSING_JWT_TOKEN,
								msg: {
									original: undefined,
									custom: this.tag.errorManager.createErrorMessageByCode( TagErrorCode.MISSING_JWT_TOKEN )
								},
								error: undefined
							}
						},
						this,
						this.name
					)
				);

				setTimeout( () => { throw new Error( message ); }, 0 );
			}
		);
	};

	/**
	 * @description Executes the video load chain. Called directly on first execution and
	 * re-invoked by #drainPendingSlot when the pending slot is drained.
	 */
	#execute = ( params: IParamsVO, callbacks: ICallbacksVO, onceSettings: ITagOnceSettings ): Promise<IPromiseResolveGeneric> => {
		this?.logger?.log( VideoLoadManager.#LOG_PREFIX, 'paramsVO:', params, 'callbacksVO:', callbacks, 'onceSettings:', onceSettings );

		Measure.end( Metrics.createToLoadTime );
		Measure.start( Metrics.loadToAttachResourceTime );

		this.#videoAPIUtilities.mergeWhitelistedLoadParams( params );

		delete params.TAG;
		delete params.SKIN;

		params = this.tag.tagUtilities.normalizeParams( params );
		this.tag.callbacks = this.tag.tagUtilities.mergeCallbacks( this.tag.callbacks, callbacks );

		if ( this.utils.Type.isObject( onceSettings ) ) {
			this.tagMVC.model.onceSettingsManager.onceSettings = this.utils.DeepMerge.deepmerge( this.tagMVC.model.onceSettingsManager.onceSettings, onceSettings );
		}

		if ( this?.tag?.params?.TAG?.loadingAnimationConfig?.backgroundImage?.enabled ) {
			this.#videoAPIUtilities.setLoadingAnimationBackgroundImage( params );
		}

		return new Promise(
			( resolve, reject ) => {
				this.#videoAPIUtilities.stopPlayer( params ).then(
					( data ) => {
						this?.logger?.log( VideoLoadManager.#LOG_PREFIX, 'stopPlayer: resolved:', data.msg );
						this.utils.Delay.delay().then(
							() => {
								this?.logger?.log( VideoLoadManager.#LOG_PREFIX, 'dispatch:', TagEvent.ON_REQUEST_TO_CLEAR_ERROR );
								this.tag.dispatchTagEvent(
									this.tag.getTagEventVO(
										TagEvent.ON_REQUEST_TO_CLEAR_ERROR,
										this.tag,
										{ paramsVO: params },
										this,
										this.name
									)
								);
							}
						).then(
							() => {
								if ( this.#isJwtTokenMissing( params ) ) {
									this.#dispatchMissingJwtFatalError( resolve, params );
									return;
								}
								switch ( this.#videoAPIUtilities.allowedToLoadVideo() ) {
									case true:
										this?.logger?.log( VideoLoadManager.#LOG_PREFIX, 'dispatch:', TagEvent.ON_API_LOAD_VIDEO_REQUEST );
										this.tag.dispatchTagEvent(
											this.tag.getTagEventVO(
												TagEvent.ON_API_LOAD_VIDEO_REQUEST,
												this.tag,
												{ paramsVO: params },
												this,
												this.name
											)
										);
										resolve( { msg: 'done' } );
										break;
									case false:
										this?.logger?.warn( VideoLoadManager.#LOG_PREFIX, 'not allowed to load video' );
										this.#rectifyStateOnFailure( LoadRequestReason.DISALLOWED, reject );
										break;
								}
							}
						);
					}
				).catch(
					( error ) => {
						this?.logger?.warn( 'load: ERROR:', error );
						this.#rectifyStateOnFailure( LoadRequestReason.STOP_PLAYER_FAILED, reject, error );
					}
				);
			}
		);
	};

	/**
	 * @description Fires on ON_PLAYER_EVENT. Stamps playbackAt when RESOURCE_START fires —
	 * the earliest signal that the player has committed to the new resource.
	 * Uses PlayerAdapterEvent (Smart Tag's abstraction) rather than avia-js PlayerEvent directly.
	 */
	#onPlayerEvent = ( event: TagEventVO ): void => {
		switch ( event?.data?.playerEvent?.type ) {
			case PlayerAdapterEvent.RESOURCE_START:
				if ( this.#awaitingPlayback ) {
					this.#awaitingPlayback.playbackAt = Date.now();
					this.#awaitingPlayback = null;
				}
				break;
		}
	};

	/**
	 * @description Fires on ON_CREATE_PLAYER_SUCCESS or ON_CREATE_PLAYER_FAILURE.
	 * Success resets concurrency immediately after stamping history. An active-load failure stamps
	 * history, waits one listener-safe tick, clears standard content properties when possible, then
	 * releases concurrency and drains. Events without an active load keep the immediate behavior.
	 */
	#onPlayerCreationComplete = ( event: TagEventVO ): void => {
		let shouldClearFailedLoadContent = false;
		if ( this.#activeRecord ) {
			if ( event?.type === TagEvent.ON_CREATE_PLAYER_SUCCESS ) {
				this.#activeRecord.playerReadyAt = Date.now();
				// Keep reference for playbackAt stamp — RESOURCE_START fires after this handler completes.
				// The record is already in history so stamping it later is reflected in the display.
				this.#awaitingPlayback = this.#activeRecord;
			} else {
				this.#activeRecord.reason = LoadRequestReason.CREATION_FAILED;
				this.#awaitingPlayback = null;
				shouldClearFailedLoadContent = true;
			}
			this.#pushToHistory( this.#activeRecord );
			this.#activeRecord = null;
		}

		if ( shouldClearFailedLoadContent ) {
			this.#clearContentAfterPlayerCreationFailure()
				.catch( ( error ) => {
					this?.logger?.warn( VideoLoadManager.#LOG_PREFIX, 'creation failure cleanup ERROR:', error );
				} )
				.finally( this.#releaseCreationGuardAndDrain );
			return;
		}

		this.#releaseCreationGuardAndDrain();
	};

}